import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
} from "react";
import type { BoardDragState } from "./boardDragState";

// dnd-kit core has no programmatic cancel, so this fakes the Escape its
// keyboard sensor listens for on the document. `key` is left out on purpose:
// the sensor matches `code`, so other Escape handlers reading `key` ignore it.
const cancelKeyboardDrag = () =>
    document.dispatchEvent(
        new KeyboardEvent("keydown", { code: "Escape", bubbles: true }),
    );

const isFocusLost = () =>
    document.activeElement === null || document.activeElement === document.body;

/**
 * Keeps keyboard focus on the dragged card, and on the moved card after a
 * drop. Moving a card to another column remounts it, so after each state
 * change the card is focused again if focus fell to the page. During and
 * after a keyboard drag it is also scrolled into view; a pointer drop
 * leaves the scroll where the pointer put it.
 * A keyboard drag cancels when focus goes anywhere else: another element,
 * a pointer press, or the window losing focus.
 */
export const useBoardDragFocus = <TItem>(state: BoardDragState<TItem>) => {
    const keyboardDragKey =
        state.phase === "dragging" && state.isKeyboard ? state.activeKey : null;
    const request = useMemo(() => {
        if (state.phase === "dragging") {
            return state.isKeyboard
                ? { placementKey: state.activeKey, shouldScroll: true }
                : null;
        }
        return state.phase === "idle" && state.focusKey !== null
            ? { placementKey: state.focusKey, shouldScroll: state.isKeyboard }
            : null;
    }, [state]);
    const activatorsRef = useRef(new Map<string, HTMLElement>());

    const registerActivator = useCallback(
        (placementKey: string, node: HTMLElement | null) => {
            if (node === null) {
                activatorsRef.current.delete(placementKey);
            } else {
                activatorsRef.current.set(placementKey, node);
            }
        },
        [],
    );

    useLayoutEffect(() => {
        const node =
            request === null
                ? undefined
                : activatorsRef.current.get(request.placementKey);
        if (request === null || node === undefined) {
            return;
        }
        if (request.shouldScroll) {
            node.scrollIntoView({ block: "nearest", inline: "nearest" });
        }
        if (isFocusLost()) {
            node.focus({ preventScroll: true });
        }
    }, [request]);

    useEffect(() => {
        if (keyboardDragKey === null) {
            return undefined;
        }
        const handleFocusIn = ({ target }: FocusEvent) => {
            const card = activatorsRef.current.get(keyboardDragKey);
            if (!(target instanceof Node) || card?.contains(target) !== true) {
                cancelKeyboardDrag();
            }
        };
        document.addEventListener("focusin", handleFocusIn, true);
        document.addEventListener("pointerdown", cancelKeyboardDrag, true);
        window.addEventListener("blur", cancelKeyboardDrag);
        return () => {
            document.removeEventListener("focusin", handleFocusIn, true);
            document.removeEventListener(
                "pointerdown",
                cancelKeyboardDrag,
                true,
            );
            window.removeEventListener("blur", cancelKeyboardDrag);
        };
    }, [keyboardDragKey]);

    return registerActivator;
};
